import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Box, Checkbox, IconButton, Stack } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  type ColumnFiltersState,
  type ColumnPinningState,
  type ColumnSizingState,
  type ColumnVisibilityState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type Updater,
  flexRender,
  stockFeatures,
  useTable,
  tableFeatures,
  createSortedRowModel,
  columnFilteringFeature,
  globalFilteringFeature,
  createFilteredRowModel,
  rowPaginationFeature,
  createPaginatedRowModel,
  columnVisibilityFeature

} from '@tanstack/react-table';
import _ from 'lodash';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ValidationError } from 'yup';
import { useAppSelector } from '@/store/hooks';
import { selectGridState, updateGridState } from '@/store/reducers/gridsSlice';
import { GridFilterPanel } from './CustomGridFilterPanel';
import { GridWrapper } from './grid.styles';
import { GridColActions, isNewRow } from './GridColActions';
import { GridPagination } from './GridPagination';
import { ColumnHeaderMenu } from './overlays/ColumnHeaderMenu';
import { formatValidation, newGridRecordId } from './overlays/commonFunctions';
import { LoadingOverlay } from './overlays/LoadingOverlay';
import { NoResultsOverlay } from './overlays/NoResultsOverlay';
import { NoRowsOverlay } from './overlays/NoRowsOverlay';
import { ShowColumnsPanel } from './overlays/ShowColumnsPanel';
import { filterFnByType } from './utility/filterFns';
import { columnFiltersToLegacyFilterModel, legacySortModelToSortingState, sortingStateToLegacySortModel } from './utility/legacyAdapters';
import type { IPersistedGridState, IPhoenixGridProps, TPhoenixCellContext, TPhoenixColumnDef } from './utility/types';


interface IGridRowContext {
  errors: { [key: string]: string | undefined };
  resetError: (field: string) => void;
  resetErrors: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const GridRowContext = createContext<IGridRowContext>({
  errors: {},
  resetError: () => null,
  resetErrors: () => null,
});

const normalizeColumnDef = (col: any, index: number): TPhoenixColumnDef => {
  const accessorKey = col.accessorKey ?? col.field;
  const id = col.id ?? accessorKey ?? `col_${index}`;
  const header = col.header ?? col.headerName ?? id;
  const legacyValueFormatter = col.valueFormatter;

  if (!col.id && !col.accessorKey && !col.field && typeof col.header !== 'string') {

    console.error(
      `[PhoenixGrid] Column at index ${index} has no id/accessorKey/field and a non-string header — falling back to a generated id ("${id}"). Give this column an explicit \`id\` (or \`accessorKey\`) to keep sorting/filtering/pinning stable across renders.`
    );
  }

  return {
    ...col,
    id,
    ...(accessorKey ? { accessorKey } : {}),
    header,
    cell:
      col.cell ??
      (col.renderCell
        ? (ctx: TPhoenixCellContext) =>
          col.renderCell({
            id: ctx.row.id,
            row: ctx.row.original,
            value: ctx.getValue(),
            formattedValue: legacyValueFormatter ? legacyValueFormatter({ value: ctx.getValue() }) : ctx.getValue(),
          })
        : undefined),
    meta: {
      ...(legacyValueFormatter && !col.meta?.valueFormatter
        ? { valueFormatter: ({ value }: { value: any }) => legacyValueFormatter({ value }) }
        : {}),
      ...col.meta,
    },
  };
};
export const CPGrid = ({
  name = '',
  height,
  rows,
  columns,
  actions,
  pageSize = 10,
  rowsPerPageOptions,
  canNotEdit,
  canNotCopy,
  disablePointer = false,
  pinnedLeft,
  pinnedRight,
  rowActionsSlot,
  validateValues,
  validateSchema,
  isFullHeightColumn,
  hideFooter,
  onFilterModelChange,
  gridBorderColor,
  isFlexible = false,
  autoHeight,
  gridId,
  gridHighlightColor = '',
  paginationIconColor = '',
  sortModel,
  onSortModelChange,
  getRowId,
  rowCount,
  paginationMode = 'client',
  sortingMode = 'client',
  filterMode = 'client',
  selectionModel,
  onSelectionModelChange,
  checkboxSelection,
  onRowClick,
  loading, }: IPhoenixGridProps) => {
  const gridName = gridId || name;
  const dispatch = useDispatch();
  const savedState = useAppSelector(selectGridState(gridName)) as IPersistedGridState | undefined;
  const [localRows, setLocalRows] = useState<Record<string, any>[]>(rows || []);
  const [editableRowId, setEditableRowId] = useState<string | number | null>(null);
  const [rowDraft, setRowDraft] = useState<Record<string, any> | null>(null);
  const [editableRowError, setEditableRowErrors] = useState<{ [key: string]: string | undefined }>({});
  const [filterLogic, setFilterLogic] = useState<'and' | 'or'>('and');
  const [sorting, setSorting] = useState<SortingState>(() => savedState?.sorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => savedState?.columnFilters ?? []);
  const [pagination, setPagination] = useState<PaginationState>(
    () => savedState?.pagination ?? { pageIndex: 0, pageSize: 10 }
  );
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    () => savedState?.columnPinning ?? { start: pinnedLeft ?? [], end: (pinnedRight ?? []).concat(['options']) }
  );
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() => savedState?.columnVisibility ?? {});
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [filterPanelAnchorEl, setFilterPanelAnchorEl] = useState<HTMLElement | null>(null);
  const [filterPanelField, setFilterPanelField] = useState<string | undefined>(undefined);
  const [showColumnsAnchorEl, setShowColumnsAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setLocalRows(rows || []);
  }, [rows]);

  const handleOpenFilterPanel = useCallback((anchorEl: HTMLElement, columnId: string) => {
    setFilterPanelField(columnId);
    setFilterPanelAnchorEl(anchorEl);
  }, []);
  const handleCloseFilterPanel = useCallback(() => setFilterPanelAnchorEl(null), []);
  const handleOpenShowColumns = useCallback((anchorEl: HTMLElement) => setShowColumnsAnchorEl(anchorEl), []);
  const handleCloseShowColumns = useCallback(() => setShowColumnsAnchorEl(null), []);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const lastSortModelRef = useRef<typeof sortModel>(sortModel);
  useEffect(() => {
    if (sortModel && !_.isEqual(sortModel, lastSortModelRef.current)) {
      lastSortModelRef.current = sortModel;
      setSorting(legacySortModelToSortingState(sortModel));
    }
  }, [sortModel]);


  const rowSelection = useMemo<RowSelectionState>(
    () => Object.fromEntries((selectionModel ?? []).map((id) => [String(id), true])),
    [selectionModel]
  );
  const handleResetError = useCallback((field: string) => {
    setEditableRowErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);
  const handleResetErrors = useCallback(() => setEditableRowErrors({}), []);

  const resolveRowId = useCallback((row: any) => (getRowId ? getRowId(row) : row?.id), [getRowId]);

  const commitSave = useCallback(
    async (row: Record<string, any>) => {
      const response: any = await actions?.onSave?.(row);

      if (!response?.error) {
        handleResetErrors();
        setEditableRowId(null);
        setRowDraft(null);
        setLocalRows((prev) => prev.map((r) => (resolveRowId(r) === resolveRowId(row) ? row : r)));
        return true;
      }
      return false;
    },
    [actions, handleResetErrors, resolveRowId]
  );

  const validateIfValidationExists = useCallback(
    (row: Record<string, any>) => {
      try {
        validateSchema?.validateSync(row, { abortEarly: false });
      } catch (error) {
        const errors = (error as { inner: ValidationError[] }).inner;
        setEditableRowErrors(formatValidation(errors));
        return false;
      } if (validateValues) {
        return validateValues(row);
      }

      setEditableRowErrors({});
      return true;
    },
    [validateSchema, validateValues]
  );
  const handleSaveClick = useCallback(
    async (row: Record<string, any>) => {
      const merged = { ...row, ...rowDraft };
      const isValid = validateIfValidationExists(merged);
      if (isValid) {
        await commitSave(merged);
      } else {
        // keep the row in edit mode so the user can fix validation errors
      }
    },
    [rowDraft, validateIfValidationExists, commitSave]
  );

  const handleStartEdit = useCallback(
    (row: Record<string, any>) => {
      const rowId = resolveRowId(row);

      if (editableRowId !== null && isNewRow({ id: editableRowId })) {
        setLocalRows((prev) => prev.filter((r) => resolveRowId(r) !== editableRowId));
      }
      handleResetErrors();

      if (actions?.onEdit) {
        actions.onEdit(row);
      }

      setEditableRowId(rowId);
      setRowDraft(row);
    },
    [editableRowId, resolveRowId, handleResetErrors, actions]
  );

  const handleCancelClick = useCallback(
    (row: Record<string, any>) => {
      handleResetErrors();
      setEditableRowId(null);
      setRowDraft(null);
      if (isNewRow(row)) {
        setLocalRows((prev) => prev.filter((r) => resolveRowId(r) !== resolveRowId(row)));
      }
      actions?.onCancel?.(row);
    },
    [handleResetErrors, resolveRowId, actions]
  );

  const handleDuplicate = useCallback(
    (row: Record<string, any>) => {
      if (editableRowId !== null && isNewRow({ id: editableRowId })) {
        setLocalRows((prev) => prev.filter((r) => resolveRowId(r) !== editableRowId));
      }
      handleResetErrors();
      if (actions?.onCopy) {
        actions.onCopy(row);
        return;
      }
      const duplicated = { ...row, id: newGridRecordId, _id: row.id, isNew: true };
      setLocalRows((prev) => [...prev, duplicated]);
      setEditableRowId(newGridRecordId);
      setRowDraft(duplicated);
      setTimeout(() => setPagination((prev) => ({ ...prev, pageIndex: Math.floor(localRows.length / prev.pageSize) })));
    },
    [editableRowId, resolveRowId, actions, localRows.length]
  );

  const handleDelete = useCallback(
    (row: Record<string, any>) => {
      setLocalRows((prev) => prev.filter((r) => resolveRowId(r) !== resolveRowId(row)));
      actions?.onDelete?.(row);
    },
    [resolveRowId, actions]
  );
  const features = tableFeatures({
    ...stockFeatures,
    sortedRowModel: createSortedRowModel(),
    columnFilteringFeature,
    globalFilteringFeature,
    filteredRowModel: createFilteredRowModel(),
    rowPaginationFeature,
    paginatedRowModel: createPaginatedRowModel(),
    columnVisibilityFeature
  });

  const setDraftValue = useCallback((field: string, value: any) => {
    setRowDraft((prev) => ({ ...(prev ?? {}), [field]: value }));
    setEditableRowErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const preparedColumns = useMemo<TPhoenixColumnDef[]>(
    () =>
      columns.map((col, index) => {
        const normalized = normalizeColumnDef(col, index);
        const type = normalized.meta?.type ?? 'string';
        return {
          ...normalized,
          filterFn: filterFnByType[type] ?? filterFnByType.string,
        };
      }),
    [columns]
  );

  const actionsColumn: TPhoenixColumnDef | null = actions
    ? {
      id: 'options',
      header: 'Actions',
      enableSorting: false,
      enableColumnFilter: false,
      size: 105,
      cell: (ctx) => {
        const rowId = resolveRowId(ctx.row.original);
        const isEditMode = editableRowId === rowId;
        return (
          <GridColActions
            name={name}
            ctx={ctx}
            canNotCopy={canNotCopy}
            canNotEdit={canNotEdit}
            rowActionsSlot={rowActionsSlot}
            pageSize={pageSize}
            isEditMode={isEditMode}
            onStartEdit={handleStartEdit}
            onSaveClick={handleSaveClick}
            onCancelClick={handleCancelClick}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            canDelete={!!actions?.onDelete}
          />
        );
      },
    }
    : null;

  const selectionColumn: TPhoenixColumnDef | null = checkboxSelection
    ? {
      id: '__select__',
      header: ({ table: t }) => (
        <Checkbox
          checked={t.getIsAllRowsSelected()}
          indeterminate={t.getIsSomeRowsSelected() && !t.getIsAllRowsSelected()}
          onClick={(event) => event.stopPropagation()}
          onChange={t.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onClick={(event) => event.stopPropagation()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 48,
      enableSorting: false,
      enableColumnFilter: false,
    }
    : null;

  const cols = useMemo(
    () => [selectionColumn, ...preparedColumns, actionsColumn].filter((c): c is TPhoenixColumnDef => !!c),

    [selectionColumn, preparedColumns, actionsColumn, editableRowId]
  );


  const table = useTable({
    features: features,
    data: localRows,
    columns: cols,
    state: { sorting, columnFilters, pagination, columnPinning, rowSelection, columnVisibility, columnSizing, },
    getRowId: getRowId ?? ((row) => row?.id),
    manualSorting: sortingMode === 'server',
    manualFiltering: filterMode === 'server',
    manualPagination: paginationMode === 'server',
    autoResetPageIndex: false,
    pageCount: paginationMode === 'server' && rowCount !== undefined ? Math.max(1, Math.ceil(rowCount / pagination.pageSize)) : undefined,
    enableRowSelection: !!checkboxSelection,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater: Updater<ColumnFiltersState>) => {
      setColumnFilters((prev) =>
        typeof updater === 'function' ? updater(prev) : updater
      );

      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
      }));
    },
    onPaginationChange: setPagination,
    onColumnPinningChange: setColumnPinning,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      onSelectionModelChange?.(Object.keys(next).filter((key) => next[key]));
    },
    meta: {
      editableRowId,
      rowDraft,
      errors: editableRowError,
      setDraftValue,
      resetError: handleResetError,
    },
  });

  const hasNoVisibleColumns = table.getVisibleLeafColumns().length === 0;
  const onSortModelChangeRef = useRef(onSortModelChange);
  const onFilterModelChangeRef = useRef(onFilterModelChange);

  useEffect(() => {
    onSortModelChangeRef.current = onSortModelChange;
    onFilterModelChangeRef.current = onFilterModelChange;
  });

  useEffect(() => {
    onSortModelChangeRef.current?.(sortingStateToLegacySortModel(sorting));
  }, [sorting]);

  useEffect(() => {
    const filterModel = columnFiltersToLegacyFilterModel(columnFilters);

    onFilterModelChangeRef.current?.({
      ...filterModel,
      linkOperator: filterLogic,
    });
  }, [columnFilters, filterLogic]);

  // restore/save grid state to redux, mirrors onStateChange/handleSaveGridState
  useEffect(() => {
    const nextState: IPersistedGridState = {
      sorting,
      columnFilters,
      pagination,
      columnPinning,
      columnVisibility,
    };
    const isStateChanged = !_.isEqual(nextState, savedState);

    if (gridName && isStateChanged) {
      dispatch(updateGridState({ gridName, gridState: nextState }));
    }

  }, [gridName, sorting, columnFilters, pagination, columnPinning, columnVisibility]);

  const pageCount = table.getPageCount();
  useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex >= pageCount) {
      setPagination((prev) => ({ ...prev, pageIndex: pageCount - 1 }));
    }
  }, [pageCount, pagination.pageIndex]);


  return (
    <>
      <GridWrapper
        $height={height}
        $gridHighlightColor={gridHighlightColor}
        $enabled={!!height || !localRows?.length}
        $isFullHeightColumn={isFullHeightColumn}
        $hideFooter={hideFooter}
        $isFlexible={isFlexible}
        $gridBorderColor={gridBorderColor}
        $disablePointer={disablePointer}
        $autoHeight={autoHeight}
      >
        <GridRowContext value={{ errors: editableRowError, resetError: handleResetError, resetErrors: handleResetErrors }}>

          <TableContainer ref={tableContainerRef} sx={autoHeight ? { overflow: 'visible' } : { flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: table.getTotalSize(),
                minWidth: '100%',
              }}>
              <TableHead>
                {hasNoVisibleColumns && (
                  <TableRow>
                    <TableCell style={{ backgroundColor: '#ececec' }}>&nbsp;</TableCell>
                  </TableRow>
                )}
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow hover key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const pinned = header.column.getIsPinned();
                      return (
                        <TableCell
                          key={header.id}
                          className={pinned ? `phoenix-grid-cell-pinned-${pinned}` : undefined}
                          style={{
                            width: header.getSize(),
                            position: 'relative',
                            overflow: 'hidden',
                            paddingRight: 4,
                            backgroundColor: '#ececec',
                            ...(pinned
                              ? {
                                '--pin-offset': `${pinned === 'start' ? header.column.getStart('start') : header.column.getAfter('end')}px`,
                              }
                              : {}),
                          }}
                        >
                          {header.isPlaceholder ? null : (
                            <Stack direction="row" alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                </Box>

                                {header.column.getCanFilter() && !!(header.column.getFilterValue() as any)?.value && (
                                  <>
                                    <IconButton onClick={(event) => handleOpenFilterPanel(event.currentTarget, header.column.id)}>
                                      <FilterAltIcon
                                        fontSize="small"
                                        color="primary"
                                      />
                                    </IconButton>
                                  </>
                                )}

                                {header.column.getCanSort() && (
                                  <TableSortLabel
                                    hideSortIcon={false}
                                    active={!!header.column.getIsSorted()}
                                    direction={header.column.getIsSorted() || 'asc'}
                                    // active={!!sorting.find((s) => s.id === header.column.id)}
                                    // direction={sorting.find((s) => s.id === header.column.id)?.desc ? 'desc' : 'asc'}
                                    onClick={header.column.getToggleSortingHandler()}
                                    sx={{
                                      ml: 0,
                                      flexShrink: 0,
                                      '& .MuiTableSortLabel-icon': { color: 'grey', fontSize: 16, margin: 0 },
                                      '&.Mui-active .MuiTableSortLabel-icon': { color: 'grey' },
                                    }}
                                  />
                                )}
                              </Stack>

                              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', mr: '6px' }}>
                                <ColumnHeaderMenu
                                  column={header.column}
                                  onOpenFilter={handleOpenFilterPanel}
                                  onOpenShowColumns={handleOpenShowColumns}
                                />
                              </Box>
                            </Stack>
                          )}
                          {header.column.getCanResize() && (
                            <Box
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              onDoubleClick={() => header.column.resetSize()}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                marginX: 0.5,
                                width: 10,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                cursor: 'col-resize',
                                userSelect: 'none',
                                touchAction: 'none',
                                zIndex: 1,
                                '&::after': {
                                  content: '""',
                                  width: '2px',
                                  height: '50%',
                                  backgroundColor: header.column.getIsResizing() ? '#000' : '#c8c8c8',
                                  borderRadius: 1,
                                  transition: 'background-color 120ms',
                                },
                                '&:hover::after': {
                                  backgroundColor: '#000',
                                },
                              }}
                            />
                          )}

                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => onRowClick?.({ row: row.original })}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const pinned = cell.column.getIsPinned();
                      return (
                        <TableCell
                          key={cell.id}
                          className={pinned ? `phoenix-grid-cell-pinned-${pinned}` : undefined}
                          style={{
                            width: cell.column.getSize(),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'left',

                            backgroundColor: '#fff',
                            ...(pinned
                              ? {
                                '--pin-offset': `${pinned === 'start' ? cell.column.getStart('start') : cell.column.getAfter('end')}px`,
                              }
                              : {}),
                          }}
                        >

                          {flexRender(cell.column.columnDef.cell, cell.getContext())}

                        </TableCell>
                      );
                    })}
                    {hasNoVisibleColumns && <TableCell>&nbsp;</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {loading && <LoadingOverlay />}
            {!loading &&
              table.getRowModel().rows.length === 0 &&
              (columnFilters.length > 0 ? <NoResultsOverlay /> : <NoRowsOverlay />)}
          </TableContainer>

          {!hideFooter && (
            <div className="phoenix-grid-footer">
              <GridPagination
                table={table}
                paginationIconBgColor={gridHighlightColor}
                paginationIconColor={paginationIconColor}
                rowsPerPageOptions={rowsPerPageOptions || [pageSize]}
              />
            </div>
          )}

          {filterPanelAnchorEl && (
            <GridFilterPanel
              table={table}
              anchorEl={filterPanelAnchorEl}
              tableContainerRef={tableContainerRef}
              initialField={filterPanelField}
              filterLogic={filterLogic}
              onFilterLogicChange={setFilterLogic}
              onClose={handleCloseFilterPanel}
            />
          )}
          <ShowColumnsPanel
            table={table}
            tableContainerRef={tableContainerRef}
            anchorEl={showColumnsAnchorEl}
            onClose={handleCloseShowColumns} />
        </GridRowContext>
      </GridWrapper >
    </>
  );
};
